import React, {useMemo} from 'react';
import {Alert, Box, Chip, Collapse, Paper, Stack, Typography} from '@mui/material';
import {buildCollectionEeiRows} from '../buildCollectionCoverageView';
import CoverageHoverBar from '../analytic/CoverageHoverBar';
import CollectionMaterialRow from './CollectionMaterialRow';

function EeiChip({label}) {
    return (
        <Chip
            size="small"
            label={label}
            variant="outlined"
            sx={{height: 20, fontSize: '0.65rem', fontFamily: 'Roboto Mono, monospace'}}
        />
    );
}

function eeiRowKey(row) {
    return String(row.eeiId || row.eeiNumber || `eei-${row.eeiIndex}`);
}

function MaterialSection({title, materials, showRelevance = false, onOpenMaterial}) {
    if (!materials.length) {
        return (
            <Box>
                <Typography variant="caption" fontWeight={700} display="block" gutterBottom>
                    {title}
                </Typography>
                <Typography variant="caption" color="text.secondary">None</Typography>
            </Box>
        );
    }
    return (
        <Box>
            <Typography variant="caption" fontWeight={700} display="block" gutterBottom>
                {title} ({materials.length})
            </Typography>
            <Stack spacing={1}>
                {materials.map((material) => (
                    <CollectionMaterialRow
                        key={`${title}-${material.id}`}
                        material={material}
                        showRelevance={showRelevance}
                        onOpen={onOpenMaterial}
                    />
                ))}
            </Stack>
        </Box>
    );
}

function EeiCard({
    row,
    open,
    onToggle,
    onOpenMaterial,
}) {
    const hasMaterials = row.linkedMaterials.length > 0 || row.unlinkedMaterials.length > 0;
    const materialCount = row.linkedMaterials.length + row.unlinkedMaterials.length;
    const materialLabel = materialCount === 1
        ? '1 material'
        : `${materialCount} materials`;

    return (
        <Paper
            variant="outlined"
            sx={{
                p: 1.5,
                cursor: hasMaterials ? 'pointer' : 'default',
            }}
            onClick={hasMaterials ? () => onToggle?.(open ? null : eeiRowKey(row)) : undefined}
        >
            <Stack spacing={1}>
                <Stack direction="row" spacing={1} alignItems="flex-start" useFlexGap>
                    <EeiChip label={row.eeiNumber || row.label}/>
                    <Typography variant="body2" fontWeight={600} sx={{flex: 1, minWidth: 0, lineHeight: 1.45}}>
                        {row.text || 'Untitled EEI'}
                    </Typography>
                </Stack>

                <CoverageHoverBar
                    value={row.generalScorePct ?? row.linkedScorePct}
                    explanation={row.explanation}
                />

                {hasMaterials && (
                    <>
                        <Typography variant="caption" color="primary.main">
                            {open ? 'Hide' : 'Show'} {materialLabel}
                        </Typography>
                        <Collapse in={open}>
                            <Stack spacing={2} mt={0.5} onClick={(e) => e.stopPropagation()}>
                                <MaterialSection
                                    title="Linked materials"
                                    materials={row.linkedMaterials}
                                    onOpenMaterial={onOpenMaterial}
                                />
                                <MaterialSection
                                    title="Suggested by Minsky"
                                    materials={row.unlinkedMaterials}
                                    showRelevance
                                    onOpenMaterial={onOpenMaterial}
                                />
                            </Stack>
                        </Collapse>
                    </>
                )}
            </Stack>
        </Paper>
    );
}

/**
 * EEI tab: one expandable row per EEI. The expanded row is URL-driven
 * (?coverage=collection&tab=eei&eei=<id>) so C2 returnTo restores it after
 * viewing a material. Material rows open the CM/IIR viewer (contract C2).
 */
export default function CollectionCoverageEeisTab({
    view,
    pirEeis = null,
    expandedEeiId = null,
    onToggleEei,
    onOpenMaterial,
}) {
    const rows = useMemo(
        () => buildCollectionEeiRows(view, pirEeis),
        [view, pirEeis],
    );

    if (!view) {
        return (
            <Alert severity="info">
                Collection coverage has not been computed for this ICR yet.
            </Alert>
        );
    }

    if (rows.length === 0) {
        return (
            <Typography variant="body2" color="text.secondary">
                No EEIs found for this ICR. Add EEIs to populate collection coverage.
            </Typography>
        );
    }

    return (
        <Stack spacing={1.5}>
            <Typography variant="body2" color="text.secondary">
                Each EEI shows its linked materials and materials suggested by Minsky (CMs and IIRs).
            </Typography>
            {rows.map((row) => {
                const key = eeiRowKey(row);
                const open = expandedEeiId != null && key === String(expandedEeiId);

                return (
                    <EeiCard
                        key={key}
                        row={row}
                        open={open}
                        onToggle={onToggleEei}
                        onOpenMaterial={onOpenMaterial}
                    />
                );
            })}
        </Stack>
    );
}
